import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  ArrowLeft, ExternalLink, MessageCircle, Sparkles, Target,
  TrendingUp, Users, Zap, AlertTriangle, CheckCircle2, RefreshCw,
  Calendar, Globe, Code2, Boxes, Loader2, Image as ImageIcon, Link2, Layers, FileText,
  Search, Download, Hash,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const GithubIcon = ({ size = 16, color = 'currentColor', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={style} aria-hidden="true">
    <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.38 7.86 10.9.58.1.79-.25.79-.55v-2.1c-3.2.69-3.87-1.36-3.87-1.36-.52-1.33-1.28-1.68-1.28-1.68-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.76 2.7 1.25 3.36.95.1-.74.4-1.25.73-1.54-2.55-.29-5.24-1.28-5.24-5.68 0-1.25.45-2.28 1.18-3.08-.12-.29-.51-1.46.11-3.05 0 0 .96-.31 3.15 1.18.91-.25 1.89-.38 2.87-.38s1.96.13 2.87.38c2.19-1.49 3.15-1.18 3.15-1.18.62 1.59.23 2.76.11 3.05.73.8 1.18 1.83 1.18 3.08 0 4.41-2.69 5.39-5.25 5.67.41.36.78 1.07.78 2.15v3.19c0 .3.21.66.79.55A11.5 11.5 0 0 0 23.5 12C23.5 5.65 18.35.5 12 .5z"/>
  </svg>
);

function fmtFull(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return '—'; }
}

const POTENTIAL_COLOR = (label = '') => {
  const s = (label || '').toLowerCase();
  if (s.includes('exceptional')) return '#22C55E';
  if (s.includes('high')) return '#7C3AED';
  if (s.includes('medium')) return '#FFB020';
  if (s.includes('low')) return '#EF4444';
  return '#A855F7';
};

export default function ProjectDetailView({ item, onBack, onOpenInBuild, onRescrape }) {
  if (item.kind === 'scrape') {
    return <ScrapeDetail item={item} onBack={onBack} onRescrape={onRescrape} />;
  }
  return <ProjectDetail item={item} onBack={onBack} onOpenInBuild={onOpenInBuild} />;
}

/* =================== Project (build / import) =================== */
function ProjectDetail({ item, onBack, onOpenInBuild }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/projects/${item.id}/details`, { credentials: 'include' });
      if (r.ok) setDetails(await r.json());
    } finally { setLoading(false); }
  }, [item.id]);

  useEffect(() => { load(); }, [load]);

  const project = details?.project || item.raw || {};
  const sessions = details?.sessions || [];

  const kindIcon = item.kind === 'import' ? Code2 : Boxes;
  const kindColor = item.kind === 'import' ? '#A855F7' : '#7C3AED';
  const KindIcon = kindIcon;

  return (
    <div style={{ height: '100%', overflowY: 'auto', color: '#0F0F1A' }}>
      <div style={{ padding: '24px 40px 40px', maxWidth: 1400, margin: '0 auto' }}>
        {/* Top bar */}
        <button
          onClick={onBack}
          data-testid="project-back-btn"
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: 'none', color: 'rgba(15,15,30,0.55)',
            fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0, marginBottom: 22,
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#fff'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(15,15,30,0.55)'}
        >
          <ArrowLeft size={13} /> Back to Workspace
        </button>

        {/* Hero */}
        <div style={{
          display: 'flex', gap: 22, padding: 28, marginBottom: 22,
          background: 'rgba(15,15,30,0.025)',
          border: '1px solid rgba(15,15,30,0.06)',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: 0, right: 0, width: 220, height: 220,
            background: `radial-gradient(circle at top right, ${kindColor}15, transparent 70%)`,
            pointerEvents: 'none',
          }} />
          <div style={{
            width: 64, height: 64, flexShrink: 0,
            background: `${kindColor}18`, border: `1px solid ${kindColor}40`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <KindIcon size={26} color={kindColor} />
          </div>
          <div style={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{
                fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
                color: kindColor, textTransform: 'uppercase',
                padding: '2px 6px', background: `${kindColor}12`, border: `1px solid ${kindColor}30`,
              }}>{item.kind === 'import' ? 'Imported repo' : 'Built project'}</span>
              <span style={{ fontSize: 11, color: 'rgba(15,15,30,0.48)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Calendar size={11} /> Created {fmtFull(project.created_at)}
              </span>
            </div>
            <h1 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 8px', letterSpacing: '-0.01em' }}>
              {project.full_name || project.name || 'Untitled'}
            </h1>
            <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
              {project.framework && <Chip label={project.framework} color={kindColor} />}
              {project.type && project.type !== project.framework && <Chip label={project.type} />}
              {project.status && <Chip label={project.status} color={project.status === 'ready' ? '#22C55E' : '#FFB020'} />}
              {sessions.length > 0 && <Chip label={`${sessions.length} session${sessions.length > 1 ? 's' : ''}`} color="#06B6D4" />}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', zIndex: 1 }}>
            <button
              onClick={() => onOpenInBuild && onOpenInBuild(project)}
              data-testid="open-in-build-btn"
              style={primaryBtn}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 28px rgba(124,58,237,0.4)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            >
              <Zap size={13} /> Open in Build
            </button>
            {project.repo_url && (
              <a href={project.repo_url} target="_blank" rel="noopener noreferrer"
                data-testid="open-repo-btn"
                style={ghostBtn}>
                <GithubIcon size={13} /> View on GitHub <ExternalLink size={11} />
              </a>
            )}
          </div>
        </div>

        {/* Single-column layout — business intelligence lives in Monetize now. */}
        <div>
          {/* Sessions */}
          <div>
            <SectionHeader title={`Sessions (${sessions.length})`} subtitle="Build conversations with the AI" />
            <div style={{
              background: 'rgba(15,15,30,0.025)',
              border: '1px solid rgba(15,15,30,0.06)',
            }}>
              {sessions.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'rgba(15,15,30,0.48)', fontSize: 11 }}>
                  No sessions yet — open this project in Build to start chatting with the AI.
                </div>
              ) : (
                sessions.map(s => (
                  <button
                    key={s.chat_id}
                    onClick={() => onOpenInBuild && onOpenInBuild(project, s.chat_id)}
                    data-testid={`session-${s.chat_id}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '12px 14px', textAlign: 'left',
                      background: 'transparent', border: 'none',
                      borderBottom: '1px solid rgba(15,15,30,0.035)',
                      cursor: 'pointer', color: '#0F0F1A', fontFamily: 'inherit',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(15,15,30,0.03)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <MessageCircle size={13} color="rgba(15,15,30,0.48)" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {s.title}
                      </div>
                      <div style={{ fontSize: 10, color: 'rgba(15,15,30,0.48)', marginTop: 2 }}>
                        {s.message_count} msg · {fmtFull(s.updated_at || s.created_at)}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Repo info */}
          {(project.repo_url || project.framework) && (
            <div style={{ marginTop: 18 }}>
              <SectionHeader title="Repository" />
              <div style={{
                padding: 16, background: 'rgba(15,15,30,0.025)',
                border: '1px solid rgba(15,15,30,0.06)',
                fontSize: 12, lineHeight: 1.7,
              }}>
                {project.repo_url && (
                  <RepoRow label="Source" value={
                    <a href={project.repo_url} target="_blank" rel="noopener noreferrer"
                      style={{ color: '#06B6D4', textDecoration: 'none' }}>
                      {project.repo_url.replace('https://github.com/', '')}
                    </a>
                  } />
                )}
                {project.framework && <RepoRow label="Framework" value={project.framework} />}
                {project.entry_file && <RepoRow label="Entry" value={<code>{project.entry_file}</code>} />}
                {project.local_path && <RepoRow label="Local" value={<code style={{ fontSize: 10 }}>{project.local_path}</code>} />}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =================== Scrape detail =================== */
function ScrapeDetail({ item, onBack, onRescrape }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('cards'); // cards | links | images | headings | intel
  const [search, setSearch] = useState('');
  const [intel, setIntel] = useState(null);
  const [intelLoading, setIntelLoading] = useState(false);
  const [intelGenerating, setIntelGenerating] = useState(false);
  const [intelError, setIntelError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/scrapes/${item.id}/details`, { credentials: 'include' });
        if (r.ok) setData(await r.json());
      } finally { setLoading(false); }
    })();
  }, [item.id]);

  // Pre-load cached competitive analysis (if any)
  useEffect(() => {
    let cancelled = false;
    setIntelLoading(true);
    (async () => {
      try {
        const r = await fetch(`${API}/api/scrapes/${item.id}/competitive`, { credentials: 'include' });
        if (r.ok && !cancelled) setIntel(await r.json());
      } finally { if (!cancelled) setIntelLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [item.id]);

  const generateIntel = async (regenerate = false) => {
    setIntelGenerating(true);
    setIntelError('');
    try {
      const r = await fetch(`${API}/api/scrapes/${item.id}/competitive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ regenerate }),
      });
      if (r.ok) {
        setIntel(await r.json());
      } else {
        const err = await r.json().catch(() => ({}));
        setIntelError(err.detail || 'Generation failed');
      }
    } catch (e) {
      setIntelError(e.message || 'Network error');
    } finally {
      setIntelGenerating(false);
    }
  };

  const cards = data?.cards || [];
  const links = data?.links || [];
  const images = data?.images || [];
  const headings = data?.headings || [];
  const hasRichData = cards.length > 0 || links.length > 0 || images.length > 0;

  const filteredCards = useMemo(() => {
    if (!search.trim()) return cards;
    const q = search.toLowerCase().trim();
    return cards.filter(c =>
      (c.title || '').toLowerCase().includes(q)
      || (c.description || '').toLowerCase().includes(q)
      || (c.href || '').toLowerCase().includes(q)
    );
  }, [cards, search]);

  const filteredLinks = useMemo(() => {
    if (!search.trim()) return links;
    const q = search.toLowerCase().trim();
    return links.filter(l =>
      (l.text || '').toLowerCase().includes(q)
      || (l.href || '').toLowerCase().includes(q)
    );
  }, [links, search]);

  const exportJSON = () => {
    const payload = JSON.stringify({
      url: data?.final_url, title: data?.title, captured_at: data?.created_at,
      counts: data?.counts, tech_stack: data?.tech_stack,
      cards, links, images, headings,
    }, null, 2);
    const blob = new Blob([payload], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${data?.title || 'scrape'}-${item.id}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const exportCSV = () => {
    if (cards.length === 0) return;
    const esc = (s = '') => `"${String(s).replace(/"/g, '""')}"`;
    const rows = [
      ['title', 'description', 'href', 'image'].join(','),
      ...cards.map(c => [esc(c.title), esc(c.description), esc(c.href), esc(c.image)].join(',')),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${data?.title || 'scrape'}-cards.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ height: '100%', overflowY: 'auto', color: '#0F0F1A' }}>
      <div style={{ padding: '24px 40px 40px', maxWidth: 1400, margin: '0 auto' }}>
        <button onClick={onBack} data-testid="scrape-back-btn" style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'transparent', border: 'none', color: 'rgba(15,15,30,0.55)',
          fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0, marginBottom: 22,
        }}>
          <ArrowLeft size={13} /> Back to Workspace
        </button>

        {/* Hero */}
        <div style={{
          display: 'flex', gap: 22, padding: 28, marginBottom: 22,
          background: 'rgba(15,15,30,0.025)',
          border: '1px solid rgba(15,15,30,0.06)',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: 0, right: 0, width: 220, height: 220,
            background: 'radial-gradient(circle at top right, rgba(6,182,212,0.15), transparent 70%)',
            pointerEvents: 'none',
          }} />
          {data?.favicon ? (
            <img src={data.favicon} alt="" style={{
              width: 64, height: 64, flexShrink: 0, objectFit: 'contain',
              background: 'rgba(6,182,212,0.08)', border: '1px solid rgba(6,182,212,0.3)',
              padding: 12,
            }} onError={e => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div style={{
              width: 64, height: 64, flexShrink: 0,
              background: 'rgba(6,182,212,0.18)', border: '1px solid rgba(6,182,212,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Globe size={26} color="#06B6D4" />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{
                fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
                color: '#06B6D4', textTransform: 'uppercase',
                padding: '2px 6px', background: 'rgba(6,182,212,0.12)', border: '1px solid rgba(6,182,212,0.3)',
              }}>Web scrape</span>
              {data?.domain && (
                <span style={{ fontSize: 11, color: 'rgba(15,15,30,0.55)', fontFamily: "'JetBrains Mono', monospace" }}>
                  {data.domain}
                </span>
              )}
              <span style={{ fontSize: 11, color: 'rgba(15,15,30,0.48)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Calendar size={11} /> {fmtFull(data?.created_at)}
              </span>
            </div>
            <h1 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 8px', letterSpacing: '-0.01em' }}>
              {data?.title || item.title}
            </h1>
            {(data?.description || item.subtitle) && (
              <p style={{ fontSize: 13, color: 'rgba(15,15,30,0.7)', lineHeight: 1.55, margin: 0, maxWidth: 760 }}>
                {data?.description || item.subtitle}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              {(data?.tech_stack || []).slice(0, 10).map(t => <Chip key={t} label={t} color="#06B6D4" />)}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', zIndex: 1 }}>
            <a href={data?.final_url || item.url} target="_blank" rel="noopener noreferrer"
              data-testid="open-scrape-source-btn"
              style={primaryBtn}>
              <ExternalLink size={13} /> Open source
            </a>
            {onRescrape && (
              <button
                onClick={() => onRescrape(data?.final_url || item.url)}
                data-testid="rescrape-btn"
                style={ghostBtn}>
                <RefreshCw size={12} /> Re-scrape
              </button>
            )}
            <button onClick={exportJSON} data-testid="export-json-btn" style={ghostBtn}>
              <Download size={12} /> Export JSON
            </button>
            {cards.length > 0 && (
              <button onClick={exportCSV} data-testid="export-csv-btn" style={ghostBtn}>
                <Download size={12} /> Export CSV
              </button>
            )}
          </div>
        </div>

        {/* KPI grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, marginBottom: 22 }}>
          <KPI label="Cards" value={data?.counts?.cards || 0} icon={<Boxes size={13} />} color="#22C55E" />
          <KPI label="Images" value={data?.counts?.images || 0} icon={<ImageIcon size={13} />} color="#06B6D4" />
          <KPI label="Links" value={data?.counts?.links || 0} icon={<Link2 size={13} />} color="#22D3EE" />
          <KPI label="Headings" value={data?.counts?.headings || 0} icon={<Layers size={13} />} color="#A855F7" />
          <KPI label="Words" value={data?.counts?.words || 0} icon={<FileText size={13} />} color="#FFB020" />
          <KPI label="Elapsed" value={`${(data?.elapsed_s || 0).toFixed(1)}s`} icon={<Loader2 size={13} />} color="#fff" />
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'rgba(15,15,30,0.48)' }}>Loading…</div>
        ) : !hasRichData ? (
          <LegacyEmptyState onRescrape={onRescrape} url={data?.final_url || item.url} />
        ) : (
          <>
            {/* Tabs + search */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <div style={{ display: 'flex', border: '1px solid rgba(15,15,30,0.08)' }}>
                {[
                  { k: 'cards', label: `Cards (${cards.length})` },
                  { k: 'links', label: `Links (${links.length})` },
                  { k: 'images', label: `Images (${images.length})` },
                  { k: 'headings', label: `Headings (${headings.length})` },
                  { k: 'intel', label: `Intel${intel ? ' ✦' : ''}` },
                ].map(t => (
                  <button
                    key={t.k}
                    onClick={() => { setTab(t.k); setSearch(''); }}
                    data-testid={`scrape-tab-${t.k}`}
                    style={{
                      padding: '8px 14px',
                      background: tab === t.k ? 'rgba(6,182,212,0.1)' : 'transparent',
                      border: 'none',
                      color: tab === t.k ? '#06B6D4' : 'rgba(15,15,30,0.65)',
                      fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {(tab === 'cards' || tab === 'links') && (
                <div style={{
                  flex: 1, display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 14px', background: 'rgba(15,15,30,0.035)',
                  border: '1px solid rgba(15,15,30,0.08)',
                }}>                  <Search size={13} color="rgba(15,15,30,0.48)" />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={tab === 'cards' ? 'Filter cards by title, description, URL…' : 'Filter links…'}
                    data-testid="scrape-search-input"
                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#0F0F1A', fontSize: 12, fontFamily: 'inherit' }}
                  />
                  {search && (
                    <span style={{ fontSize: 10, color: 'rgba(15,15,30,0.48)' }}>
                      {tab === 'cards' ? filteredCards.length : filteredLinks.length} match{(tab === 'cards' ? filteredCards.length : filteredLinks.length) === 1 ? '' : 'es'}
                    </span>
                  )}
                </div>
              )}
            </div>

            {tab === 'cards' && <CardsGrid cards={filteredCards} />}
            {tab === 'links' && <LinksList links={filteredLinks} />}
            {tab === 'images' && <ImagesGrid images={images} />}
            {tab === 'headings' && <HeadingsList headings={headings} />}
            {tab === 'intel' && (
              <CompetitiveIntel
                intel={intel}
                loading={intelLoading}
                generating={intelGenerating}
                error={intelError}
                cardsCount={cards.length}
                onGenerate={() => generateIntel(false)}
                onRegenerate={() => generateIntel(true)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CardsGrid({ cards }) {
  const [visible, setVisible] = useState(48);
  if (cards.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'rgba(15,15,30,0.48)', fontSize: 12,
        background: 'rgba(15,15,30,0.025)', border: '1px dashed rgba(15,15,30,0.08)' }}>
        No cards match your search.
      </div>
    );
  }
  return (
    <div>
      <div style={{
        display: 'grid', gap: 12,
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      }}>
        {cards.slice(0, visible).map((c, i) => (
          <CardItem key={`${c.href || c.title}-${i}`} card={c} />
        ))}
      </div>
      {visible < cards.length && (
        <button
          onClick={() => setVisible(v => v + 48)}
          data-testid="cards-load-more-btn"
          style={{
            display: 'block', margin: '20px auto 0', padding: '10px 20px',
            background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.25)',
            color: '#06B6D4', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
            cursor: 'pointer', letterSpacing: '0.03em',
          }}
        >
          Load more ({cards.length - visible} remaining)
        </button>
      )}
    </div>
  );
}

function CardItem({ card }) {
  const [imgError, setImgError] = useState(false);
  return (
    <div data-testid="scrape-card" style={{
      background: 'rgba(15,15,30,0.025)',
      border: '1px solid rgba(15,15,30,0.06)',
      display: 'flex', flexDirection: 'column',
      transition: 'all 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(6,182,212,0.35)'; e.currentTarget.style.background = 'rgba(6,182,212,0.04)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(15,15,30,0.06)'; e.currentTarget.style.background = 'rgba(15,15,30,0.025)'; }}
    >
      <div style={{
        width: '100%', aspectRatio: '16 / 9',
        background: 'rgba(6,182,212,0.05)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', borderBottom: '1px solid rgba(15,15,30,0.035)',
      }}>
        {card.image && !imgError ? (
          <img
            src={card.image}
            alt={card.title || ''}
            loading="lazy"
            onError={() => setImgError(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <ImageIcon size={28} color="rgba(6,182,212,0.3)" />
        )}
      </div>
      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
        <div style={{
          fontSize: 13, fontWeight: 700, color: '#0F0F1A',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {card.title || 'Untitled'}
        </div>
        {card.description && (
          <div style={{
            fontSize: 11, color: 'rgba(15,15,30,0.6)', lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {card.description}
          </div>
        )}
        {card.href && (
          <a
            href={card.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="scrape-card-link"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              marginTop: 'auto', paddingTop: 8,
              fontSize: 10, color: '#06B6D4', textDecoration: 'none',
              fontFamily: "'JetBrains Mono', monospace",
              wordBreak: 'break-all',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#22D3EE'}
            onMouseLeave={e => e.currentTarget.style.color = '#06B6D4'}
          >
            <ExternalLink size={10} />
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: 220,
            }}>{card.href.replace(/^https?:\/\//, '')}</span>
          </a>
        )}
      </div>
    </div>
  );
}

function LinksList({ links }) {
  if (links.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'rgba(15,15,30,0.48)', fontSize: 12,
        background: 'rgba(15,15,30,0.025)', border: '1px dashed rgba(15,15,30,0.08)' }}>
        No links match your search.
      </div>
    );
  }
  return (
    <div style={{ background: 'rgba(15,15,30,0.025)', border: '1px solid rgba(15,15,30,0.06)' }}>
      {links.slice(0, 500).map((l, i) => (
        <a key={i} href={l.href} target="_blank" rel="noopener noreferrer"
          data-testid="scrape-link-row"
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 16px',
            borderBottom: i < links.length - 1 ? '1px solid rgba(15,15,30,0.035)' : 'none',
            textDecoration: 'none', color: '#0F0F1A', transition: 'background 0.1s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(6,182,212,0.04)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <span style={{
            fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: l.external ? '#FFB020' : '#22C55E',
            padding: '2px 6px', minWidth: 60, textAlign: 'center',
            background: l.external ? 'rgba(255,176,32,0.1)' : 'rgba(34,197,94,0.1)',
            border: `1px solid ${l.external ? 'rgba(255,176,32,0.25)' : 'rgba(34,197,94,0.25)'}`,
          }}>{l.external ? 'External' : 'Internal'}</span>
          <span style={{ fontSize: 12, fontWeight: 500, flexShrink: 0, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {l.text || '(no text)'}
          </span>
          <span style={{
            flex: 1, fontSize: 11, color: 'rgba(15,15,30,0.55)',
            fontFamily: "'JetBrains Mono', monospace",
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {l.href}
          </span>
          <ExternalLink size={11} color="rgba(15,15,30,0.4)" />
        </a>
      ))}
      {links.length > 500 && (
        <div style={{ padding: 14, textAlign: 'center', fontSize: 11, color: 'rgba(15,15,30,0.48)' }}>
          Showing first 500 of {links.length} — export JSON for the full list.
        </div>
      )}
    </div>
  );
}

function ImagesGrid({ images }) {
  if (images.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'rgba(15,15,30,0.48)', fontSize: 12,
        background: 'rgba(15,15,30,0.025)', border: '1px dashed rgba(15,15,30,0.08)' }}>
        No images captured.
      </div>
    );
  }
  return (
    <div style={{
      display: 'grid', gap: 8,
      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    }}>
      {images.slice(0, 200).map((img, i) => {
        const src = typeof img === 'string' ? img : (img?.src || img?.url);
        if (!src) return null;
        return (
          <a key={i} href={src} target="_blank" rel="noopener noreferrer"
            data-testid="scrape-image-cell"
            style={{
              aspectRatio: '1 / 1',
              background: 'rgba(15,15,30,0.025)',
              border: '1px solid rgba(15,15,30,0.06)',
              overflow: 'hidden', display: 'block',
            }}>
            <img src={src} alt="" loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={e => { e.currentTarget.style.opacity = '0.2'; }}
            />
          </a>
        );
      })}
    </div>
  );
}

function HeadingsList({ headings }) {
  if (headings.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'rgba(15,15,30,0.48)', fontSize: 12,
        background: 'rgba(15,15,30,0.025)', border: '1px dashed rgba(15,15,30,0.08)' }}>
        No headings extracted.
      </div>
    );
  }
  return (
    <div style={{ background: 'rgba(15,15,30,0.025)', border: '1px solid rgba(15,15,30,0.06)' }}>
      {headings.map((h, i) => {
        const text = typeof h === 'string' ? h : (h?.text || '');
        const level = typeof h === 'object' ? (h?.level || 'h2') : 'h2';
        const indent = ({ h1: 0, h2: 14, h3: 28, h4: 42, h5: 56, h6: 70 })[level] || 14;
        return (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 16px', paddingLeft: 16 + indent,
            borderBottom: i < headings.length - 1 ? '1px solid rgba(15,15,30,0.035)' : 'none',
          }}>
            <span style={{
              fontSize: 9, fontWeight: 700, color: '#A855F7',
              fontFamily: "'JetBrains Mono', monospace",
              padding: '2px 5px', background: 'rgba(168,85,247,0.1)',
              border: '1px solid rgba(168,85,247,0.25)', minWidth: 24, textAlign: 'center',
            }}>{level.toUpperCase()}</span>
            <span style={{ fontSize: 12, color: 'rgba(15,15,30,0.78)' }}>{text}</span>
          </div>
        );
      })}
    </div>
  );
}

function CompetitiveIntel({ intel, loading, generating, error, cardsCount, onGenerate, onRegenerate }) {
  if (loading && !intel) {
    return <div style={{ padding: 32, textAlign: 'center', color: 'rgba(15,15,30,0.48)', fontSize: 12 }}>Loading…</div>;
  }

  if (!intel && !generating) {
    return (
      <div style={{
        padding: 44, textAlign: 'center',
        background: 'rgba(168,85,247,0.04)',
        border: '1px solid rgba(168,85,247,0.18)',
      }}>
        <Sparkles size={28} color="#A855F7" style={{ margin: '0 auto 14px', display: 'block', opacity: 0.85 }} />
        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6 }}>
          Turn this directory into competitive intel
        </div>
        <div style={{ fontSize: 12, color: 'rgba(15,15,30,0.6)', maxWidth: 540, margin: '0 auto 6px', lineHeight: 1.6 }}>
          We'll feed all {cardsCount} captured competitors into Claude Sonnet 4.5 and produce a sharp brief: market signal, categories, positioning axes, gaps, opportunities & differentiation angles.
        </div>
        {cardsCount < 3 && (
          <div style={{ fontSize: 11, color: '#FFB020', marginTop: 8, marginBottom: 12 }}>
            Need at least 3 cards. Re-scrape this URL first to capture more entries.
          </div>
        )}
        {error && (
          <div style={{ fontSize: 11, color: '#EF4444', marginTop: 8, marginBottom: 12 }}>
            {error}
          </div>
        )}
        <button
          onClick={onGenerate}
          disabled={cardsCount < 3}
          data-testid="generate-intel-btn"
          style={{
            ...primaryBtn,
            marginTop: 12,
            background: cardsCount < 3 ? 'rgba(15,15,30,0.08)' : 'linear-gradient(135deg, #A855F7 0%, #7C3AED 100%)',
            color: cardsCount < 3 ? 'rgba(15,15,30,0.48)' : '#fff',
            cursor: cardsCount < 3 ? 'not-allowed' : 'pointer',
          }}>
          <Sparkles size={13} /> Generate competitive analysis
        </button>
      </div>
    );
  }

  if (generating && !intel) {
    return (
      <div style={{
        padding: 44, textAlign: 'center',
        background: 'rgba(168,85,247,0.06)',
        border: '1px solid rgba(168,85,247,0.25)',
      }}>
        <Loader2 size={28} color="#A855F7" style={{
          margin: '0 auto 14px', display: 'block',
          animation: 'spin 1s linear infinite',
        }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: '#A855F7', marginBottom: 6 }}>
          Analyzing the competitive landscape
        </div>
        <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.55)' }}>
          Reading {cardsCount} competitors, clustering by category, surfacing market gaps…
        </div>
      </div>
    );
  }

  const a = intel?.analysis || {};

  return (
    <div data-testid="competitive-intel" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header strip with regenerate */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px',
        background: 'rgba(168,85,247,0.06)',
        border: '1px solid rgba(168,85,247,0.2)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Sparkles size={14} color="#A855F7" />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0F0F1A' }}>Competitive intel</div>
            <div style={{ fontSize: 10, color: 'rgba(15,15,30,0.5)', marginTop: 2 }}>
              {intel?.competitors_analyzed || 0} of {intel?.total_competitors || 0} competitors analyzed · Generated {fmtFull(intel?.generated_at)}
            </div>
          </div>
        </div>
        <button
          onClick={onRegenerate}
          disabled={generating}
          data-testid="regen-intel-btn"
          style={miniBtn}>
          {generating
            ? <><Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> Regenerating</>
            : <><RefreshCw size={11} /> Regenerate</>}
        </button>
      </div>

      {/* Overview + market signal */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
        <Card icon={<Globe size={13} />} label="Market overview" color="#A855F7">
          <P>{a.overview || '—'}</P>
        </Card>
        <Card icon={<TrendingUp size={13} />} label="Market signal" color="#06B6D4">
          <div style={{ fontSize: 13, color: 'rgba(15,15,30,0.78)', lineHeight: 1.55 }}>
            {a.market_size_signal || '—'}
          </div>
        </Card>
      </div>

      {/* Categories */}
      {Array.isArray(a.categories) && a.categories.length > 0 && (
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(15,15,30,0.48)',
            textTransform: 'uppercase', marginBottom: 10,
          }}>
            Categories ({a.categories.length})
          </div>
          <div style={{
            display: 'grid', gap: 10,
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          }}>
            {a.categories.map((cat, i) => (
              <div key={i} data-testid="intel-category" style={{
                padding: 14,
                background: 'rgba(15,15,30,0.025)',
                border: '1px solid rgba(15,15,30,0.06)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F1A' }}>{cat.name}</div>
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: '#A855F7',
                    padding: '2px 7px', background: 'rgba(168,85,247,0.12)',
                    border: '1px solid rgba(168,85,247,0.3)',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>{cat.count ?? '—'}</span>
                </div>
                {Array.isArray(cat.examples) && (
                  <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.6)', lineHeight: 1.6 }}>
                    {cat.examples.slice(0, 3).join(' · ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Positioning axes */}
      {Array.isArray(a.positioning_axes) && a.positioning_axes.length > 0 && (
        <Card icon={<Target size={13} />} label="Positioning axes" color="#3B82F6">
          <ul style={listStyle}>
            {a.positioning_axes.map((p, i) => <li key={i} style={liStyle}>{p}</li>)}
          </ul>
        </Card>
      )}

      {/* Gaps + Opportunities + Differentiation — 3-col */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {Array.isArray(a.market_gaps) && a.market_gaps.length > 0 && (
          <Card icon={<AlertTriangle size={13} />} label="Market gaps" color="#FFB020">
            <ul style={listStyle}>
              {a.market_gaps.map((g, i) => <li key={i} style={liStyle}>{g}</li>)}
            </ul>
          </Card>
        )}
        {Array.isArray(a.opportunities) && a.opportunities.length > 0 && (
          <Card icon={<Zap size={13} />} label="Opportunities" color="#22C55E">
            <ul style={listStyle}>
              {a.opportunities.map((o, i) => <li key={i} style={liStyle}>{o}</li>)}
            </ul>
          </Card>
        )}
        {Array.isArray(a.differentiation_angles) && a.differentiation_angles.length > 0 && (
          <Card icon={<Sparkles size={13} />} label="Differentiation angles" color="#7C3AED">
            <ul style={listStyle}>
              {a.differentiation_angles.map((d, i) => <li key={i} style={liStyle}>{d}</li>)}
            </ul>
          </Card>
        )}
      </div>

      {/* Top picks */}
      {Array.isArray(a.top_picks) && a.top_picks.length > 0 && (
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(15,15,30,0.48)',
            textTransform: 'uppercase', marginBottom: 10,
          }}>
            Top picks
          </div>
          <div style={{
            display: 'grid', gap: 10,
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          }}>
            {a.top_picks.map((pick, i) => (
              <div key={i} data-testid="intel-toppick" style={{
                padding: 14,
                background: 'rgba(124,58,237,0.04)',
                border: '1px solid rgba(124,58,237,0.2)',
                display: 'flex', flexDirection: 'column', gap: 6,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle2 size={13} color="#7C3AED" />
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F1A' }}>{pick.name}</div>
                </div>
                {pick.why && (
                  <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.65)', lineHeight: 1.55 }}>
                    {pick.why}
                  </div>
                )}
                {pick.url && (
                  <a href={pick.url} target="_blank" rel="noopener noreferrer"
                    style={{
                      fontSize: 10, color: '#06B6D4',
                      fontFamily: "'JetBrains Mono', monospace",
                      textDecoration: 'none',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      marginTop: 'auto', paddingTop: 4,
                      wordBreak: 'break-all',
                    }}>
                    <ExternalLink size={10} />
                    {String(pick.url).replace(/^https?:\/\//, '').slice(0, 36)}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risks */}
      {Array.isArray(a.risks_for_new_entrants) && a.risks_for_new_entrants.length > 0 && (
        <Card icon={<AlertTriangle size={13} />} label="Risks for new entrants" color="#EF4444">
          <ul style={listStyle}>
            {a.risks_for_new_entrants.map((r, i) => <li key={i} style={liStyle}>{r}</li>)}
          </ul>
        </Card>
      )}
    </div>
  );
}

function LegacyEmptyState({ onRescrape, url }) {
  return (
    <div style={{
      padding: 48, textAlign: 'center',
      background: 'rgba(15,15,30,0.025)',
      border: '1px dashed rgba(15,15,30,0.1)',
    }}>
      <Hash size={28} color="rgba(6,182,212,0.6)" style={{ margin: '0 auto 14px', display: 'block' }} />
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
        Detailed data not available for this scrape
      </div>
      <div style={{ fontSize: 12, color: 'rgba(15,15,30,0.55)', maxWidth: 460, margin: '0 auto 18px', lineHeight: 1.55 }}>
        This scrape was captured before the detailed payload was persisted. Re-run it to extract every card, link and image with their descriptions and redirect URLs.
      </div>
      {onRescrape && url && (
        <button
          onClick={() => onRescrape(url)}
          data-testid="legacy-rescrape-btn"
          style={primaryBtn}>
          <RefreshCw size={12} /> Re-scrape now
        </button>
      )}
    </div>
  );
}

/* ============ Pieces ============ */
function SectionHeader({ title, subtitle, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 12, marginTop: 4 }}>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(15,15,30,0.48)', textTransform: 'uppercase' }}>
          {title}
        </div>
        {subtitle && <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.48)', marginTop: 4 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function Card({ icon, label, color, children }) {
  return (
    <div style={{
      padding: 16, background: 'rgba(15,15,30,0.025)',
      border: '1px solid rgba(15,15,30,0.06)',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 10, color, fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: 13, color: 'rgba(15,15,30,0.78)', lineHeight: 1.55 }}>
        {children}
      </div>
    </div>
  );
}

function P({ children }) {
  return <div style={{ fontSize: 13, color: 'rgba(15,15,30,0.72)', lineHeight: 1.55 }}>{children}</div>;
}

function Chip({ label, color = '#fff' }) {
  return (
    <span style={{
      display: 'inline-block',
      fontSize: 10, padding: '4px 10px',
      color, background: `${color}10`,
      border: `1px solid ${color}30`,
      fontWeight: 600,
    }}>{label}</span>
  );
}

function KPI({ label, value, icon, color, small }) {
  return (
    <div style={{
      padding: 16, background: 'rgba(15,15,30,0.025)',
      border: '1px solid rgba(15,15,30,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 10, color: 'rgba(15,15,30,0.48)', letterSpacing: '0.08em', textTransform: 'uppercase',
      }}>
        <span style={{ color }}>{icon}</span> {label}
      </div>
      <div style={{
        fontSize: small ? 12 : 22, fontWeight: 800,
        color, marginTop: 6,
        fontFamily: small ? 'inherit' : "'JetBrains Mono', monospace",
      }}>
        {value}
      </div>
    </div>
  );
}

function RepoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <div style={{ width: 80, color: 'rgba(15,15,30,0.48)', fontSize: 11 }}>{label}</div>
      <div style={{ flex: 1, color: 'rgba(15,15,30,0.78)', fontSize: 12, wordBreak: 'break-all' }}>{value}</div>
    </div>
  );
}

function BuildingDeck() {
  const steps = ['Fetching context', 'Analyzing market', 'Computing positioning', 'Drafting deck'];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep(s => (s + 1) % steps.length), 1500);
    return () => clearInterval(id);
  }, [steps.length]);
  return (
    <div style={{
      padding: 36, textAlign: 'center',
      background: 'rgba(124,58,237,0.04)', border: '1px solid rgba(124,58,237,0.2)',
    }}>
      <Loader2 size={26} color="#7C3AED" style={{ margin: '0 auto 14px', display: 'block', animation: 'spin 1s linear infinite' }} />
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, color: '#7C3AED' }}>
        Generating strategic deck
      </div>
      <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.55)' }}>
        {steps[step]}…
      </div>
    </div>
  );
}

const primaryBtn = {
  padding: '10px 16px',
  background: 'linear-gradient(135deg, #7C3AED 0%, #00A88C 100%)',
  color: '#000', border: 'none',
  fontSize: 12, fontWeight: 700,
  cursor: 'pointer', fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 6,
  letterSpacing: '0.02em',
  transition: 'all 0.2s', textDecoration: 'none',
};

const ghostBtn = {
  padding: '8px 14px',
  background: 'transparent',
  border: '1px solid rgba(15,15,30,0.1)',
  color: 'rgba(15,15,30,0.72)',
  fontSize: 11, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 6,
  textDecoration: 'none',
};

const miniBtn = {
  padding: '6px 12px',
  background: 'rgba(15,15,30,0.035)',
  border: '1px solid rgba(15,15,30,0.08)',
  color: 'rgba(15,15,30,0.72)',
  fontSize: 10, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 5,
};

const listStyle = {
  margin: 0, padding: 0,
  paddingLeft: 18,
  color: 'rgba(15,15,30,0.72)',
  fontSize: 12, lineHeight: 1.7,
};

const liStyle = { marginBottom: 4 };
